import React, { useState } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const QuickBookingForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    checkIn: new Date(),
    checkOut: new Date(),
    guests: 1,
    bookingType: '',
    packageDuration: '', // changed from packageType
    safariTime: '',
    roomType: '',
    message: ''
  });

  const [showAlert, setShowAlert] = useState(false);
  const [alertVariant, setAlertVariant] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');

  const roomTypes = [
    "Standard Room",
    "Deluxe Room",
    "Suite",
    "Family Room",
    "Villa"
  ];

  const packageDurations = [
    "1 Night / 2 Days",
    "2 Nights / 3 Days",
    "3 Nights / 4 Days"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Ensure dates are properly formatted
    const formattedData = {
      ...formData,
      checkIn: formData.checkIn.toISOString(),
      checkOut: formData.checkOut.toISOString()
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/quick-book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedData),
      });

      const data = await response.json();

      if (response.ok) {
        setAlertVariant('success');
        setAlertMessage('Booking inquiry sent successfully! We will contact you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          checkIn: new Date(),
          checkOut: new Date(),
          guests: 1,
          bookingType: '',
          packageDuration: '',
          safariTime: '',
          roomType: '',
          message: ''
        });
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        throw new Error(data.message || 'Something went wrong');
      }
    } catch (error) {
      setAlertVariant('danger');
      setAlertMessage('Failed to send booking inquiry. Please try again.');
    }
    setShowAlert(true);
  };

  return (
    <div className="quick-booking-form">
      {showAlert && (
        <Alert 
          variant={alertVariant} 
          onClose={() => setShowAlert(false)} 
          dismissible
        >
          {alertMessage}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Booking Type</Form.Label>
          <Form.Select
            value={formData.bookingType}
            onChange={(e) => setFormData({...formData, bookingType: e.target.value})}
            required
          >
            <option value="">Select Booking Type</option>
            <option value="hotel">Hotel Stay</option>
            <option value="safari">Safari</option>
            <option value="package">Tour Package</option>
          </Form.Select>
        </Form.Group>

        {formData.bookingType === 'hotel' && (
          <Form.Group className="mb-3">
            <Form.Label>Room Type</Form.Label>
            <Form.Select
              value={formData.roomType}
              onChange={(e) => setFormData({...formData, roomType: e.target.value})}
              required
            >
              <option value="">Select Room Type</option>
              {roomTypes.map((type, index) => (
                <option key={index} value={type}>{type}</option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {formData.bookingType === 'safari' && (
          <Form.Group className="mb-3">
            <Form.Label>Safari Time</Form.Label>
            <Form.Select
              value={formData.safariTime}
              onChange={(e) => setFormData({...formData, safariTime: e.target.value})}
              required
            >
              <option value="">Select Safari Time</option>
              <option value="morning">Morning (6:30 AM - 9:30 AM)</option>
              <option value="evening">Evening (2:30 PM - 5:30 PM)</option>
            </Form.Select>
          </Form.Group>
        )}

        {formData.bookingType === 'package' && (
          <Form.Group className="mb-3">
            <Form.Label>Package Type</Form.Label>
            <Form.Select
              value={formData.packageDuration}
              onChange={(e) => setFormData({...formData, packageDuration: e.target.value})}
              required
            >
              <option value="">Select Package Type</option>
              {packageDurations.map((duration, index) => (
                <option key={index} value={duration}>{duration}</option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Phone</Form.Label>
          <Form.Control
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            required
          />
        </Form.Group>

        <div className="row">
          <div className="col-md-6">
            <Form.Group className="mb-3">
              <Form.Label>Check-in Date</Form.Label>
              <DatePicker
                selected={formData.checkIn}
                onChange={(date) => setFormData({...formData, checkIn: date})}
                className="form-control"
                minDate={new Date()}
                required
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group className="mb-3">
              <Form.Label>Check-out Date</Form.Label>
              <DatePicker
                selected={formData.checkOut}
                onChange={(date) => setFormData({...formData, checkOut: date})}
                className="form-control"
                minDate={formData.checkIn}
                required
              />
            </Form.Group>
          </div>
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Number of Guests</Form.Label>
          <Form.Control
            type="number"
            min="1"
            value={formData.guests}
            onChange={(e) => setFormData({...formData, guests: parseInt(e.target.value)})}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Message/Special Requests</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={formData.message}
            onChange={(e) => setFormData({...formData, message: e.target.value})}
          />
        </Form.Group>

        <Button 
          type="submit" 
          style={{
            backgroundColor: '#D4AF37',
            border: 'none',
            width: '100%',
            padding: '12px'
          }}
        >
          Submit Booking Request
        </Button>
      </Form>
    </div>
  );
};

export default QuickBookingForm;
